import type { TicketChatMessage } from "../Stores/Ticket";
import { isCaptureLogsNotice } from "./CaptureRequest";
import { isSettledWidgetAnswer } from "./SettledWidgetAnswer";
import { isBotTeamReply } from "./MessageAuthor";

// Is this comment authored by the customer side (the widget user)? Dashboard /
// agent messages carry `user`; bot messages are type BOT. Everything else
// (BOT_REPLY / SHARED_COMMENT / USER_TEXT / BOT_INPUT_REPLY) is the customer
// speaking. Failed sends don't count — no reply will ever come for them.
const isCustomerAuthored = (comment?: TicketChatMessage) =>
  !!comment &&
  comment.type !== "BOT" &&
  !comment.user &&
  !isBotTeamReply(comment) &&
  !comment.error;

// Does the timeline currently end on an unanswered customer message — i.e. the
// bot owes a reply? Returns that tail comment's id (used to key the waiting
// indicator's timeout per turn), or undefined when nothing is owed.
//
// Hidden workflow comments are skipped: they never render, so their arrival
// must not count as "the bot responded" — the customer would see the waiting
// indicator vanish with nothing taking its place. The diagnostic-logs notice
// (a system line, see isCaptureLogsNotice) is skipped for the same reason.
export const getAwaitingReplyTailId = (
  comments: TicketChatMessage[] | null | undefined,
): string | undefined => {
  if (!comments || comments.length === 0) {
    return undefined;
  }

  for (let i = comments.length - 1; i >= 0; i--) {
    const comment = comments[i];
    if (comment?.data?.hiddenAction || isCaptureLogsNotice(comment)) {
      continue;
    }
    if (!isCustomerAuthored(comment)) {
      return undefined;
    }
    return answersHandledCard(comments, i) ? undefined : comment.id;
  }

  return undefined;
};

// A tap on the feature-request card that the server settles itself (Upvote,
// Submit request — kai-intake CONTRACT §5.2): the card changes in place and
// Kai does not answer, so the customer's echo owes no reply.
const answersHandledCard = (
  comments: TicketChatMessage[],
  tailIndex: number,
): boolean => {
  for (let i = tailIndex - 1; i >= 0; i--) {
    const comment = comments[i];
    if (comment?.data?.hiddenAction || isCaptureLogsNotice(comment)) {
      continue;
    }
    // The answer step's trailing action comment (no text, no card).
    if (
      comment?.type === "BOT" &&
      !comment.data?.content &&
      !comment.data?.widget
    ) {
      continue;
    }
    const widget = comment?.type === "BOT" ? comment.data?.widget : undefined;
    return isSettledWidgetAnswer(widget?.type, widget?.response?.action);
  }
  return false;
};

// Has the customer already answered the AI step's opening question? The tail
// is the step's newest action comment. Walking back, an earlier answerbotflow
// comment of the same step with a customer message after it means the opening
// question already sits in the timeline. Another workflow step (buttons, an
// input…) before that means the AI step started after it: the button tap or
// the answer that led here is not a reply to the question, so it still shows
// (#226643).
export const hasCustomerAnsweredOpeningPrompt = (
  comments: TicketChatMessage[] | null | undefined,
): boolean => {
  const list = (comments ?? []).filter(
    (comment) => !isCaptureLogsNotice(comment),
  );
  let customerSpoke = false;
  for (let i = list.length - 2; i >= 0; i--) {
    const comment = list[i];
    const actionType =
      comment?.type === "BOT" ? comment.data?.action?.type : undefined;
    if (actionType === "answerbotflow") {
      return customerSpoke;
    }
    if (actionType && actionType !== "passtoagent") {
      return false;
    }
    if (isCustomerAuthored(comment)) {
      customerSpoke = true;
    }
  }
  return false;
};

// Did the customer open this conversation with their own message? True when the
// first visible message is theirs — e.g. a question typed on the messenger home
// that the server put first in the thread while the workflow collected data.
// Hidden workflow comments are skipped for the same reason as above.
export const isConversationOpenedByCustomer = (
  comments: TicketChatMessage[] | null | undefined,
): boolean => {
  const firstVisible = (comments ?? []).find(
    (comment) => !comment?.data?.hiddenAction,
  );
  return isCustomerAuthored(firstVisible);
};
